import {
  describesSimplifiedHouse,
  SimplifiedHouseAnswers
} from '@alpinehomeair/common';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setTotalBTUs, setTotalLatentGain } from 'features/project';
import {
  SizingEstimateRequest,
  SizingEstimateResult,
  SizingEstimateStatus
} from 'hooks/use-sizing-estimate-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'utils/api';
import { getFoundationType, getLevelCount } from 'utils/structure-basics';

const ESTIMATE_DEBOUNCE_MS = 400;

// The basic sizing flow's load estimate: a simplified Manual J on the home
// details (size, levels, foundation, age or insulation, bedrooms,
// fireplaces, windows, thermostat settings), re-run as the basics change.
// The answers go to the server raw; it reads and bounds them with the same
// builder the ducted requirement ladder uses, and the only question decided
// here — may the card be shown at all — is the shared predicate the server
// applies too (describesSimplifiedHouse). The totals are written where the
// full calculation writes its own, so the rest of the app (coverage,
// operating cost, the quote) reads one set of numbers.
export default function useSizingEstimate(enabled: boolean): {
  status: SizingEstimateStatus;
  hasRequest: boolean;
} {
  const dispatch = useAppDispatch();
  const structureUniqueId = useAppSelector(
    (state) => state.structure.structureUniqueId
  );
  const structure = useAppSelector((state) => state.structure.structure);
  const locationData = useAppSelector((state) => state.project.locationData);
  const projectSettings = useAppSelector(
    (state) => state.project.projectSettings
  );
  const [status, setStatus] = useState<SizingEstimateStatus>('idle');
  const lastRequestKey = useRef<string | null>(null);

  const request = useMemo((): SizingEstimateRequest | null => {
    if (!enabled) return null;
    const setting = (code: string) =>
      projectSettings.find((projectSetting) => projectSetting.code === code)
        ?.value;
    const structureHasLevels = structure.length > 0;

    const answers: SimplifiedHouseAnswers = {
      zipCode: locationData?.zipcode,
      squareFootage: setting('square_footage'),
      numberOfFloors: getLevelCount(structure) || setting('number_of_floors'),
      // The structure's levels are the answer once they exist; before that
      // the foundation_type answer stands in.
      foundationType: structureHasLevels
        ? getFoundationType(structure)
        : setting('foundation_type'),
      basementType: setting('basement_type'),
      yearBuilt: setting('year_built'),
      insulationLevel: setting('insulation_level'),
      bedrooms: setting('number_of_bedrooms'),
      fireplaces: setting('number_of_fireplaces'),
      windows: setting('windows'),
      desiredHeating: setting('desired_heating'),
      desiredCooling: setting('desired_cooling')
    };
    if (!describesSimplifiedHouse(answers)) return null;

    return { structureUniqueId, ...answers };
  }, [enabled, structureUniqueId, structure, locationData, projectSettings]);

  useEffect(() => {
    if (!request) return undefined;
    const requestKey = JSON.stringify(request);
    if (requestKey === lastRequestKey.current) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setStatus('loading');
      try {
        const response = await axios.post<SizingEstimateResult>(
          'sizing/get-sizing-estimate',
          request
        );
        if (cancelled) return;
        lastRequestKey.current = requestKey;
        const latentGain = Math.round(response.data.latentGain ?? 0);
        // Cooling is a total-load figure everywhere the totals are read, so
        // latent gain is folded in exactly as the full calculation's is.
        dispatch(
          setTotalBTUs({
            heating: Math.round(response.data.heatingLoad ?? 0),
            cooling: Math.round(response.data.coolingLoad ?? 0) + latentGain
          })
        );
        dispatch(setTotalLatentGain(latentGain));
        setStatus('done');
      } catch {
        if (!cancelled) setStatus('error');
      }
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [request, dispatch]);

  return {
    status,
    hasRequest: !!request
  };
}
